import React, { useCallback, useEffect, useRef } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { RadioOption } from './FormRadioGroup';
import { FormInput, FormRadioGroup } from './index';
import { DEFAULT_MAX_LENGTH } from 'const/validation';
import { twJoin } from 'tailwind-merge';

interface RadioFieldsWithOtherOptionProps<T extends FieldValues> {
  label: string;
  textLabel?: string;
  isRequired?: boolean;
  showOtherValue: string[];
  placeholder?: string;
  radioName: Path<T>;
  textInputName: Path<T>;
  options: RadioOption[];
  hiddenView?: boolean;
}

const RadioFieldsWithOtherOption = <T extends FieldValues>({
  label,
  isRequired,
  radioName,
  textInputName,
  showOtherValue,
  placeholder,
  textLabel = '',
  hiddenView = true,
  options,
}: RadioFieldsWithOtherOptionProps<T>) => {
  const { watch, setValue } = useFormContext<T>();
  const selectedValue = watch(radioName);
  const prevSelectedValue = useRef(selectedValue);

  const onChangeCallback = useCallback(() => {
    setValue(textInputName, '' as never, {
      shouldValidate: !showOtherValue.includes(selectedValue),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  useEffect(() => {
    const isValueChanged = prevSelectedValue.current !== selectedValue;
    const isInitialHydration = prevSelectedValue.current === undefined;

    if (
      isValueChanged &&
      !isInitialHydration &&
      !showOtherValue.includes(selectedValue)
    ) {
      onChangeCallback();
    }

    prevSelectedValue.current = selectedValue;
  }, [
    selectedValue,
    showOtherValue,
    textInputName,
    setValue,
    onChangeCallback,
  ]);

  return (
    <div className="flex flex-col">
      <FormRadioGroup
        isRequired={isRequired}
        label={label}
        name={radioName}
        options={options}
      />

      <div
        className={twJoin(
          'ml-10',
          hiddenView ? 'hidden' : 'opacity-50',
          showOtherValue.includes(selectedValue) && '!block !opacity-100',
        )}
      >
        <FormInput
          name={textInputName}
          maxLength={DEFAULT_MAX_LENGTH}
          labelClassName="min-h-min"
          label={textLabel}
          disabled={!showOtherValue.includes(selectedValue)}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export default RadioFieldsWithOtherOption;
